/**
 * Page-font extraction and matching, shared by cover-and-replace text edits
 * (textEdit.ts) and free-standing added text (addText.ts) so both can use
 * one of the page's own embedded fonts instead of a generic standard font —
 * extracted here rather than duplicated so there's exactly one place that
 * knows how to pull a usable font out of a PDF's /Resources.
 *
 * Font matching: pdf.js's getTextContent() (used for hit-testing/position in
 * the text-edit editor UI) doesn't expose which PDF font RESOURCE a given
 * run actually used — only an internal pdf.js identifier with no public
 * mapping back. So rather than trying to pin down "the" original font per
 * run, this tries every font already embedded on that page (extracting
 * TrueType or OpenType-wrapped-CFF fonts directly from the PDF) and uses the
 * first one whose character set actually covers the text being drawn.
 * Falls back to the closest of pdf-lib's 14 standard fonts only if nothing
 * on the page covers it.
 */
import { PDFDocument, PDFDict, PDFName, PDFStream, PDFRawStream, PDFFont, StandardFonts } from 'pdf-lib';

const STANDARD_POST_VERSIONS = new Set([0x00010000, 0x00020000, 0x00025000, 0x00030000]);

/**
 * Some legacy Mac TrueType fonts (seen: classic "Osaka") ship a `post` table
 * version outside the four the OpenType spec defines, which fontkit doesn't
 * recognize — it fails internally trying to read italicAngle off a table it
 * couldn't parse. Overwriting it in place with a minimal, valid version-3.0
 * post table (same byte length, so no other table offset needs to move)
 * fixes embedding without touching anything the font actually needs post
 * data for (PostScript glyph names — irrelevant here).
 */
function sanitizeTrueTypePostTable(font: Uint8Array): Uint8Array {
  try {
    const buf = font.slice();
    const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    const numTables = view.getUint16(4);
    for (let i = 0; i < numTables; i++) {
      const off = 12 + i * 16;
      const tag = String.fromCharCode(buf[off], buf[off + 1], buf[off + 2], buf[off + 3]);
      if (tag !== 'post') continue;
      const tableOffset = view.getUint32(off + 8);
      const tableLength = view.getUint32(off + 12);
      const version = view.getUint32(tableOffset);
      if (STANDARD_POST_VERSIONS.has(version)) return buf;
      view.setUint32(tableOffset, 0x00030000);
      buf.fill(0, tableOffset + 4, tableOffset + tableLength);
      return buf;
    }
    return buf;
  } catch {
    return font;
  }
}

async function inflateFlate(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'));
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

async function maybeInflate(stream: PDFRawStream): Promise<Uint8Array> {
  const filter = stream.dict.get(PDFName.of('Filter'));
  const isFlate = filter?.toString() === '/FlateDecode';
  return isFlate ? inflateFlate(stream.contents) : stream.contents;
}

function lookupFontDescriptor(doc: PDFDocument, fontObj: PDFDict): PDFDict | null {
  const direct = fontObj.get(PDFName.of('FontDescriptor'));
  if (direct) return doc.context.lookup(direct, PDFDict);
  // Type0 composite fonts (CID-keyed) carry the descriptor on the descendant font.
  const descFontsRef = fontObj.get(PDFName.of('DescendantFonts'));
  if (!descFontsRef) return null;
  const descFonts = doc.context.lookup(descFontsRef) as any;
  const df = doc.context.lookup(descFonts.get(0), PDFDict);
  const descriptorRef = df.get(PDFName.of('FontDescriptor'));
  return descriptorRef ? doc.context.lookup(descriptorRef, PDFDict) : null;
}

async function extractEmbeddedFontBytes(doc: PDFDocument, fontObj: PDFDict): Promise<Uint8Array | null> {
  const fd = lookupFontDescriptor(doc, fontObj);
  if (!fd) return null;

  const ff2 = fd.get(PDFName.of('FontFile2'));
  if (ff2) {
    const stream = doc.context.lookup(ff2, PDFStream) as PDFRawStream;
    const bytes = await maybeInflate(stream);
    return sanitizeTrueTypePostTable(bytes);
  }

  const ff3 = fd.get(PDFName.of('FontFile3'));
  if (ff3) {
    const stream = doc.context.lookup(ff3, PDFStream) as PDFRawStream;
    const subtype = stream.dict.get(PDFName.of('Subtype'))?.toString();
    if (subtype === '/OpenType') return maybeInflate(stream);
    // Bare CFF (/Type1C, /CIDFontType0C) isn't directly embeddable via
    // pdf-lib — would need hand-building an sfnt wrapper. Skip.
    return null;
  }

  return null;
}

export function pickStandardFont(styleHint: string | undefined): StandardFonts {
  const hint = (styleHint || '').toLowerCase();
  const bold = /bold/.test(hint);
  const italic = /italic|oblique/.test(hint);
  const monospace = /mono/.test(hint);
  const serif = /serif/.test(hint) && !/sans/.test(hint);
  if (monospace) {
    if (bold && italic) return StandardFonts.CourierBoldOblique;
    if (bold) return StandardFonts.CourierBold;
    if (italic) return StandardFonts.CourierOblique;
    return StandardFonts.Courier;
  }
  if (serif) {
    if (bold && italic) return StandardFonts.TimesRomanBoldItalic;
    if (bold) return StandardFonts.TimesRomanBold;
    if (italic) return StandardFonts.TimesRomanItalic;
    return StandardFonts.TimesRoman;
  }
  if (bold && italic) return StandardFonts.HelveticaBoldOblique;
  if (bold) return StandardFonts.HelveticaBold;
  if (italic) return StandardFonts.HelveticaOblique;
  return StandardFonts.Helvetica;
}

export interface PageFontCandidate {
  font: PDFFont;
  characterSet: Set<number>;
}

/** All directly-embeddable fonts already used on a page, keyed by page index
 * so repeated edits on the same page don't re-extract/re-embed repeatedly. */
export async function getPageFontCandidates(
  doc: PDFDocument, page: ReturnType<PDFDocument['getPage']>, pageIndex: number,
  cache: Map<number, PageFontCandidate[]>,
): Promise<PageFontCandidate[]> {
  const cached = cache.get(pageIndex);
  if (cached) return cached;

  const candidates: PageFontCandidate[] = [];
  try {
    const resources = page.node.Resources();
    const fontDict = resources?.lookup(PDFName.of('Font'), PDFDict);
    if (fontDict) {
      for (const key of fontDict.keys()) {
        try {
          const ref = fontDict.get(key);
          const fontObj = doc.context.lookup(ref, PDFDict);
          const bytes = await extractEmbeddedFontBytes(doc, fontObj);
          if (!bytes) continue;
          const font = await doc.embedFont(bytes, { subset: true });
          candidates.push({ font, characterSet: new Set(font.getCharacterSet()) });
        } catch {
          // Unusable font (unsupported table format, malformed, etc.) — skip it.
        }
      }
    }
  } catch {
    // No resources / no fonts on this page — candidates stays empty, falls back below.
  }

  cache.set(pageIndex, candidates);
  return candidates;
}

export function textCodePoints(text: string): number[] {
  return Array.from(text).map(ch => ch.codePointAt(0)!);
}

export interface ResolvedFont { font: PDFFont; isExactMatch: boolean }

/** Resolves a font for drawing `text` on `page`: the first of the page's own
 * embedded fonts that covers every character, or a standard-font fallback
 * (picked from `fontFamilyHint`) if none do. */
export async function resolveFontForText(
  doc: PDFDocument, page: ReturnType<PDFDocument['getPage']>, pageIndex: number,
  text: string, fontFamilyHint: string | undefined,
  candidateCache: Map<number, PageFontCandidate[]>, fallbackCache: Map<string, PDFFont>,
): Promise<ResolvedFont> {
  const candidates = await getPageFontCandidates(doc, page, pageIndex, candidateCache);
  const needed = textCodePoints(text);
  for (const c of candidates) {
    if (needed.every(cp => c.characterSet.has(cp))) {
      return { font: c.font, isExactMatch: true };
    }
  }
  const font = await embedStandardFallback(doc, fontFamilyHint, fallbackCache);
  return { font, isExactMatch: false };
}

export async function embedStandardFallback(
  doc: PDFDocument, fontFamilyHint: string | undefined, fallbackCache: Map<string, PDFFont>,
): Promise<PDFFont> {
  const std = pickStandardFont(fontFamilyHint);
  let font = fallbackCache.get(std);
  if (!font) {
    font = await doc.embedFont(std);
    fallbackCache.set(std, font);
  }
  return font;
}
